import { useCallback, useEffect, useState } from "react";
import {
  addExpenseContract,
  addRevenueProject,
  createRecognitionDocument,
  getDictionaryItems,
  getRecognitionDocuments,
  getRecognitionList,
  removeRecognitionItem,
} from "../../../../../api/financeApi";

const isoToUi = (value) => {
  if (!value) return "";
  const [year, month, day] = String(value).slice(0, 10).split("-");
  if (!year || !month || !day) return "";
  return `${day}.${month}.${year}`;
};

const uiToIso = (value) => {
  const [day, month, year] = String(value || "").split(".");
  if (!day || !month || !year) return null;
  return `${year}-${month}-${day}`;
};

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const loadDictionary = async (code) => {
  try {
    const items = await getDictionaryItems(code, { limit: 500 });
    return Array.isArray(items) ? items : [];
  } catch {
    return [];
  }
};

/**
 * Справочники, списки периодов и документы признания.
 * kind: revenue | expense
 */
function useRecognitionApi(kind) {
  const [state, setState] = useState({
    loading: true,
    error: null,
    data: null,
    settings: [],
    recognitions: [],
  });

  const reload = useCallback(async () => {
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const [projects, organizations, counterparties, contracts, accounts, list, documents] =
        await Promise.all([
          loadDictionary("projects"),
          loadDictionary("organizations"),
          loadDictionary("counterparties"),
          loadDictionary("contracts"),
          loadDictionary("accounts"),
          getRecognitionList(kind),
          getRecognitionDocuments({ kind }),
        ]);

      let nextId = 1;
      const projectByName = new Map();
      const contractByName = new Map();
      const projectList = [];
      const contractList = [];

      const ensureProject = (name) => {
        if (!name) return null;
        if (!projectByName.has(name)) {
          const project = {
            id: nextId,
            name,
            organization: "",
            counterparty: "",
            article: "",
          };
          nextId += 1;
          projectByName.set(name, project);
          projectList.push(project);
        }
        return projectByName.get(name);
      };

      const ensureContract = (name, projectName) => {
        if (!name) return null;
        if (!contractByName.has(name)) {
          const project = ensureProject(projectName);
          const contract = {
            id: nextId,
            name,
            projectId: project?.id || null,
            organization: "",
            counterparty: "",
            article: "",
            amount: null,
          };
          nextId += 1;
          contractByName.set(name, contract);
          contractList.push(contract);
        }
        return contractByName.get(name);
      };

      projects.forEach((item) => ensureProject(item.name));
      contracts.forEach((item) => ensureContract(item.name));

      const organizationNames = new Set(organizations.map((item) => item.name));
      const counterpartyNames = new Set(counterparties.map((item) => item.name));
      const articles = new Set(accounts.map((item) => item.name));

      const settings = (Array.isArray(list) ? list : []).map((item) => {
        const project = ensureProject(item.project);
        const contract = ensureContract(item.contract, item.project);
        if (item.organization) organizationNames.add(item.organization);
        if (item.counterparty) counterpartyNames.add(item.counterparty);
        return {
          id: nextId++,
          apiId: item.id,
          projectId: project?.id || null,
          contractId: contract?.id || null,
          organization: item.organization || "",
          counterparty: item.counterparty || "",
          startDate: isoToUi(item.start_date),
          endDate: isoToUi(item.end_date),
        };
      });

      const recognitions = (Array.isArray(documents) ? documents : []).map((item) => {
        const project = ensureProject(item.project);
        const contract = ensureContract(item.contract, item.project);
        if (item.organization) organizationNames.add(item.organization);
        if (item.counterparty) counterpartyNames.add(item.counterparty);
        if (item.article) articles.add(item.article);
        if (project) {
          project.organization = project.organization || item.organization || "";
          project.counterparty = project.counterparty || item.counterparty || "";
          project.article = project.article || item.article || "";
        }
        if (contract) {
          contract.organization = contract.organization || item.organization || "";
          contract.counterparty = contract.counterparty || item.counterparty || "";
          contract.article = contract.article || item.article || "";
          contract.amount = contract.amount ?? toNumber(item.contract_amount);
        }
        return {
          id: item.id,
          apiId: item.id,
          date: isoToUi(item.doc_date),
          projectId: project?.id || null,
          organization: item.organization || "",
          counterparty: item.counterparty || "",
          contractId: contract?.id || null,
          article: item.article || "",
          contractAmount: toNumber(item.contract_amount),
          readiness: toNumber(item.percent_complete),
          cumulative: toNumber(item.cumulative_amount),
          previous: toNumber(item.previous_amount),
          current: toNumber(item.period_amount),
          document: item.document_name || "—",
        };
      });

      const data = {
        projects: projectList,
        contracts: contractList,
        articles: [...articles].sort((a, b) => a.localeCompare(b, "ru")),
        organizations: [...organizationNames].sort((a, b) => a.localeCompare(b, "ru")),
        counterparties: [...counterpartyNames].sort((a, b) => a.localeCompare(b, "ru")),
        projectById: Object.fromEntries(projectList.map((item) => [item.id, item])),
        contractById: Object.fromEntries(contractList.map((item) => [item.id, item])),
      };

      setState({ loading: false, error: null, data, settings, recognitions });
      return data;
    } catch (error) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: error.message || "Не удалось загрузить признание",
        data: prev.data || {
          projects: [],
          contracts: [],
          articles: [],
          organizations: [],
          counterparties: [],
          projectById: {},
          contractById: {},
        },
      }));
      return null;
    }
  }, [kind]);

  useEffect(() => {
    reload();
  }, [reload]);

  const syncSettings = useCallback(
    async (next, current) => {
      const previous = current.settings;
      const catalogs = current.data;
      const previousById = new Map(previous.map((item) => [item.id, item]));
      const nextById = new Map(next.map((item) => [item.id, item]));

      for (const item of previous) {
        if (!nextById.has(item.id) && item.apiId) {
          await removeRecognitionItem(kind, item.apiId);
        }
      }

      for (const item of next) {
        const old = previousById.get(item.id);
        const same =
          old &&
          old.startDate === item.startDate &&
          old.endDate === item.endDate &&
          old.organization === item.organization &&
          old.counterparty === item.counterparty &&
          old.projectId === item.projectId &&
          old.contractId === item.contractId;
        if (same) continue;
        if (old?.apiId) await removeRecognitionItem(kind, old.apiId);
        if (kind === "revenue") {
          const project = catalogs.projectById[item.projectId];
          await addRevenueProject({
            project: project.name,
            start_date: uiToIso(item.startDate),
            end_date: uiToIso(item.endDate),
          });
        } else {
          const contract = catalogs.contractById[item.contractId];
          await addExpenseContract({
            contract: contract.name,
            organization: item.organization || null,
            counterparty: item.counterparty || null,
            start_date: uiToIso(item.startDate),
            end_date: uiToIso(item.endDate),
          });
        }
      }
      await reload();
    },
    [kind, reload]
  );

  const recognize = useCallback(
    async (draft, current) => {
      const catalogs = current.data;
      const project = catalogs.projectById[draft.projectId];
      const contract = catalogs.contractById[draft.contractId];
      const document = await createRecognitionDocument({
        kind,
        doc_date: uiToIso(draft.date),
        project: project?.name,
        organization: draft.organization,
        counterparty: draft.counterparty || null,
        contract: contract?.name || null,
        article: draft.article,
        contract_amount: draft.contractAmount,
        percent_complete: draft.readiness,
      });
      await reload();
      return document;
    },
    [kind, reload]
  );

  return { ...state, reload, syncSettings, recognize };
}

export default useRecognitionApi;
